import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { Button } from "@/components/ui/Button";
import { ChevronDownIcon } from "lucide-react";
import { FilterPill } from "./FilterPill";

export interface CompareToSelectorProps {
  compareTo: "previousYear" | null;
  onCompareToChange: (compareTo: "previousYear" | null) => void;
  className?: string;
}

export const CompareToSelector: React.FC<CompareToSelectorProps> = ({
  compareTo,
  onCompareToChange,
  className,
}) => {
  const { t } = useTranslation();

  const handleToggle = useCallback(() => {
    onCompareToChange(compareTo ? null : "previousYear");
  }, [compareTo, onCompareToChange]);

  const handleRemove = useCallback(() => {
    onCompareToChange(null);
  }, [onCompareToChange]);

  if (compareTo) {
    return (
      <FilterPill
        label={t("chart.compareTo.previousYear", "Previous Year")}
        onRemove={handleRemove}
        removeAriaLabel={t(
          "chart.compareTo.removePreviousYear",
          "Remove previous year filter",
        )}
        className={className}
        data-testid="compare-to-pill"
      />
    );
  }

  return (
    <DropdownMenu data-testid="compare-to-selector">
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={className}
          aria-label={t(
            "chart.compareTo.selectOption",
            "Select comparison option",
          )}
          data-testid="compare-button"
        >
          {t("chart.compareTo.selectOption", "Select option")}
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid="compare-dropdown">
        <DropdownMenuItem
          onClick={handleToggle}
          className="cursor-pointer"
          data-testid="compare-option-previous-year"
        >
          {t("chart.compareTo.previousYear", "Previous Year")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
